<template>
    <section class="flex flex-col gap-3" :aria-label="management?.mode === 'email' ? t('teamInvitations.pendingTitle') : undefined">
        <template v-if="management?.mode === 'email' && management.invitations.length">
            <h2 class="title-section text-ink">{{ t("teamInvitations.pendingTitle") }}</h2>
            <BaseCard padding="none" radius="md">
                <ul class="divide-y divide-border">
                    <li v-for="invitation in management.invitations" :key="invitation.email" class="flex flex-wrap items-center gap-3 p-4">
                        <div class="min-w-0 basis-full md:flex-1 md:basis-auto">
                            <p class="body-small-semibold text-ink fs-mask break-words">{{ invitation.email }}</p>
                            <p class="details text-ink-muted">{{ t("teamInvitations.invitedOn") }} <BaseDate :value="invitation.invited_at" format="date" /></p>
                        </div>
                        <StatusChip state="pending" size="2xs">{{ t("teamInvitations.pending") }}</StatusChip>
                        <BaseButton
                            theme="secondary"
                            size="sm"
                            :disabled="!!resending || sent.has(invitation.invitation_uuid)"
                            :aria-busy="resending === invitation.invitation_uuid"
                            :aria-label="
                                resending === invitation.invitation_uuid
                                    ? t('teamInvitations.sendingFor', { email: invitation.email })
                                    : sent.has(invitation.invitation_uuid)
                                      ? t('teamInvitations.sentFor', { email: invitation.email })
                                      : t('teamInvitations.resendFor', { email: invitation.email })
                            "
                            @click="resend(invitation)">
                            <span class="inline-grid place-items-center">
                                <span class="col-start-1 row-start-1" :class="{ invisible: resending === invitation.invitation_uuid || sent.has(invitation.invitation_uuid) }">{{
                                    t("teamInvitations.resend")
                                }}</span>
                                <LoadingSpinner
                                    v-if="resending === invitation.invitation_uuid"
                                    class="col-start-1 row-start-1 size-4 motion-reduce:animate-none"
                                    aria-hidden="true" />
                                <span v-else-if="sent.has(invitation.invitation_uuid)" class="col-start-1 row-start-1">{{ t("teamInvitations.sent") }}</span>
                            </span>
                        </BaseButton>
                    </li>
                </ul>
            </BaseCard>
        </template>
        <div role="alert" class="empty:sr-only">
            <template v-if="failed">
                <p class="body-small text-ink">{{ t("teamInvitations.loadError") }}</p>
                <BaseButton theme="secondary" size="sm" :disabled="loading" @click="load">{{ t("peopleDirectory.retry") }}</BaseButton>
            </template>
            <p v-else-if="actionError && management?.mode === 'email'" class="body-small text-ink">{{ actionError }}</p>
        </div>
        <p role="status" class="sr-only">{{ management?.mode === "email" ? confirmation : "" }}</p>
    </section>
</template>
<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { PendingTeamInvitation, TeamInvitationCreated, TeamInvitationResendRequest } from "~vue/api/generated/team-invitations";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDate from "~vue/components/BaseDate.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import type { TeamInvitationManagementState } from "~vue/Perspective/useTeamInvitationManagement";
import { TEAM_INTERACTION_SOURCE, teamInteractionRequest, type TeamInteractionSource } from "~vue/Teams/teamInteraction";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { onScopeDispose, ref } from "vue";
import { useI18n } from "vue-i18n";

const {
    source = TEAM_INTERACTION_SOURCE.TEAM_PAGE,
    teamId,
    invitationManagement,
} = defineProps<{ source?: TeamInteractionSource; teamId: string; invitationManagement: TeamInvitationManagementState }>();
const { t } = useI18n();
const { management, loading, failed, load } = invitationManagement;
const resending = ref("");
const actionError = ref("");
const confirmation = ref("");
const requestIds = new Map<string, string>();
const sent = ref(new Set<string>());
let active = true;
onScopeDispose(() => (active = false));
async function resend(invitation: PendingTeamInvitation) {
    const invitationId = invitation.invitation_uuid;
    if (resending.value || sent.value.has(invitationId) || management.value?.mode !== "email") {
        return;
    }
    resending.value = invitationId;
    actionError.value = "";
    confirmation.value = "";
    const requestId = requestIds.get(invitationId) ?? crypto.randomUUID();
    requestIds.set(invitationId, requestId);
    const payload: TeamInvitationResendRequest = { request_id: requestId };
    logUserInteraction(AmplitudeEventNames.TEAM_INVITATION_STARTED, { source, team_id: teamId, is_resend: true });
    try {
        const { data, error } = await useFetch(`/v1/teams/${teamId}/invitations/${invitationId}/resend`, teamInteractionRequest(source))
            .post(payload)
            .json<TeamInvitationCreated>();
        if (error.value || !data.value) {
            if (active) {
                actionError.value = t("teamInvitations.resendError");
                await load();
            }
        } else if (management.value?.mode === "email") {
            const invitationUuid = data.value.invitation_uuid;
            management.value = {
                ...management.value,
                invitations: management.value.invitations.map((pending) => (pending.invitation_uuid === invitationId ? { ...pending, invitation_uuid: invitationUuid } : pending)),
            };
            sent.value.add(invitationUuid);
            confirmation.value = t("teamInvitations.sentFor", { email: invitation.email });
        }
    } catch {
        if (active) {
            actionError.value = t("teamInvitations.resendError");
            await load();
        }
    } finally {
        resending.value = "";
    }
}
</script>
